import React from 'react';

export interface TaskCoordinates {
  lat: string;
  lon: string;
}

export interface CoordinateValidationError {
  lat?: string;
  lon?: string;
}

export const useTaskCoordinates = (
  initialLat?: number,
  initialLon?: number
) => {
  const [coords, setCoords] = React.useState<TaskCoordinates>({
    lat: initialLat !== undefined ? initialLat.toFixed(6) : '',
    lon: initialLon !== undefined ? initialLon.toFixed(6) : ''
  });
  const [errors, setErrors] = React.useState<CoordinateValidationError>({});

  const [combinedCoords, setCombinedCoords] = React.useState<string>(
    initialLat !== undefined && initialLon !== undefined
      ? `${initialLat.toFixed(6)},${initialLon.toFixed(6)}`
      : ''
  );

  React.useEffect(() => {
    setCoords({
      lat: initialLat !== undefined ? initialLat.toFixed(6) : '',
      lon: initialLon !== undefined ? initialLon.toFixed(6) : ''
    });
    if (initialLat !== undefined && initialLon !== undefined) {
      setCombinedCoords(`${initialLat.toFixed(6)},${initialLon.toFixed(6)}`);
    }
  }, [initialLat, initialLon]);

  React.useEffect(() => {
    if (coords.lat && coords.lon) {
      setCombinedCoords(`${coords.lat},${coords.lon}`);
    }
  }, [coords.lat, coords.lon]);

  // Validates lat and lon strings and returns any errors found.
  const validate = (lat: string, lon: string): CoordinateValidationError => {
    const errs: CoordinateValidationError = {};
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);

    if (lat === '' || isNaN(latNum)) {
      errs.lat = 'Latitude is required.';
    } else if (latNum < -90 || latNum > 90) {
      errs.lat = 'Latitude must be between -90 and 90.';
    }

    if (lon === '' || isNaN(lonNum)) {
      errs.lon = 'Longitude is required.';
    } else if (lonNum < -180 || lonNum > 180) {
      errs.lon = 'Longitude must be between -180 and 180.';
    }

    return errs;
  };


  const handleLatChange = (value: string) => {
    const newVal = value.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (newVal) {
      const newCoords = { lat: newVal[1], lon: newVal[2] };
      setCoords(newCoords);
      setErrors(validate(newCoords.lat, newCoords.lon));
    } else {
      setCoords((prev) => ({ ...prev, lat: value }));
      setErrors((prev) => ({ ...prev, lat: validate(value, coords.lon).lat }));
    }
  };

  const handleLonChange = (value: string) => {
    setCoords((prev) => ({ ...prev, lon: value }));
    setErrors((prev) => ({ ...prev, lon: validate(coords.lat, value).lon }));
  };


  const handleCombinedChange = (value: string) => {
    setCombinedCoords(value);
    const match = value.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (match) {
      const newCoords = { lat: match[1], lon: match[2] };
      setCoords(newCoords);
      setErrors(validate(newCoords.lat, newCoords.lon));
    }
  };

  const getValidCoords = (): [number, number] | null => {
    const errs = validate(coords.lat, coords.lon);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return null;
    }
    return [parseFloat(coords.lat), parseFloat(coords.lon)];
  };

  return {
    coords,
    errors,
    combinedCoords,
    handleLatChange,
    handleLonChange,
    handleCombinedChange,
    getValidCoords,
    hasErrors: Object.keys(validate(coords.lat, coords.lon)).length > 0
  };
};